import { ComponentProps, FC, useMemo } from 'react';
import {
  Box,
  Button,
  Colors,
  IconButton,
  Svg,
  Typography,
  useThemeValue,
} from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers';

type BannerType = 'warning' | 'info' | 'error' | 'primary-info';

type CloseProps =
  | {
      hideCloseButton?: false;
      onClose: () => void;
    }
  | {
      hideCloseButton: true;
      onClose?: undefined;
    };

type Props = CloseProps & {
  message: string;
  type?: BannerType;
  cta?: {
    label: string;
    onClick: () => void;
  };
  iconName?: string;
  isIconVisible?: boolean;
};

type ButtonVariant = ComponentProps<typeof Button>['variant'];

const typeStyleMap: Record<
  BannerType,
  { bg: keyof Colors; borderColor: keyof Colors; fill: keyof Colors; ctaVariant: ButtonVariant }
> = {
  warning: {
    bg: 'background/warning',
    borderColor: 'warning',
    fill: 'warning',
    ctaVariant: 'link/primary',
  },
  error: {
    bg: 'background/error',
    borderColor: 'error',
    fill: 'error',
    ctaVariant: 'link/primary',
  },
  info: {
    bg: 'accent/background',
    borderColor: 'accent/background',
    fill: 'primary',
    ctaVariant: 'positive',
  },
  'primary-info': {
    bg: 'primary',
    borderColor: 'primary',
    fill: 'primary/text',
    ctaVariant: 'positive',
  },
};

/**
 * Banner component renders a dismissible notification bar injected into the page layout.
 *
 * Supports three visual types (`warning`, `info`, `error`) that automatically set
 * background, border, and icon colors from the design system.
 *
 * The close button is visible by default. When the close button is visible,
 * `onClose` is required. When `hideCloseButton` is `true`, `onClose` must be
 * omitted or explicitly set to `undefined`.
 *
 * @param {string} message - The text displayed in the banner.
 * @param {BannerType} [type='warning'] - Visual style variant: `'warning'` | `'info'` | `'error'`.
 * @param {{ label: string; onClick: () => void }} [cta] - Optional call-to-action button.
 * @param {boolean} [hideCloseButton=false] - Whether the close (×) button is hidden.
 * @param {() => void} [onClose] - Close button handler. Required when `hideCloseButton` is `false`.
 * @param {string} [iconName='warning'] - Name of the leading SVG icon.
 * @param {boolean} [isIconVisible=true] - Whether the leading icon is visible.
 *
 * @example
 * // Warning banner with CTA and close button
 * <Banner
 *   message='Your trial expires in 10 days.'
 *   type='warning'
 *   cta={{ label: 'Add payment method', onClick: handlePayment }}
 *   onClose={handleClose}
 * />
 *
 * @example
 * // Error banner without icon and close button
 * <Banner
 *   message='Service unavailable.'
 *   type='error'
 *   isIconVisible={false}
 *   hideCloseButton
 * />
 *
 * @example
 * // Info banner with custom icon and close button
 * <Banner
 *   message='New feature available!'
 *   type='info'
 *   iconName='info'
 *   onClose={handleClose}
 * />
 */
export const Banner: FC<Props> = ({
  message,
  type = 'warning',
  cta,
  hideCloseButton,
  onClose,
  iconName = 'warning',
  isIconVisible = true,
}) => {
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 8;
  const styles = useMemo(() => typeStyleMap[type], [type]);

  return (
    <Box
      display='flex'
      alignItems='center'
      style={{ gap: gapXS }}
      px='m'
      py='xs'
      bg={styles.bg}
      borderRadius='m'
      borderWidth={1}
      borderStyle='solid'
      borderColor={styles.borderColor}
    >
      {isIconVisible && <Svg name={iconName} size='s' fill={styles.fill} />}
      <Typography variant='p2/light' color='primary/text' flex='1'>
        {message}
      </Typography>
      {cta && (
        <Button variant={styles.ctaVariant} onClick={cta.onClick}>
          {cta.label}
        </Button>
      )}

      {!hideCloseButton && (
        <IconButton variant='link/primary' onClick={onClose}>
          <Svg name='close' size='s' />
        </IconButton>
      )}
    </Box>
  );
};
